import React, { useState, useRef, useMemo } from "react";

import {
  BusinessInfo,
  CloseButtonBusInfo,
  DownloadButtonQR,
  InfoDescription,
  InfoTitle,
  ProfileInfoItem,
  QrCodeContainer,
  QrContent,
  QRIcon,
  QrPopover,
  ShopDetailsContainer,
  StoreName,
} from "../../styles/profile";
import { QRCodeSVG } from "qrcode.react";
import QrCodeIcon from '@mui/icons-material/QrCode';
import CloseIcon from '@mui/icons-material/Close';
import FileDownloadIcon from '@mui/icons-material/FileDownload';

import ProfileDocumentUpload, {
  FormDataState,
} from "./molecues/ProfileDocumentUpload";
import BusinessHoursDisplay from "../../components/profile/BusinessHoursDisplay";
import { DeactivateTitle } from "../../styles/PersonalInfoStep";

// Define DocumentStatus type locally
type DocumentStatus = "approved" | "pending" | "unapproved" | "not-uploaded";

const ShopDetails: React.FC<any> = ({ formData, onChange, openingHours }) => {
  const [showQrPopover, setShowQrPopover] = useState(false);

  const transformedFormData = useMemo<FormDataState>(() => {
    const getDocumentData = (
      docArray: any[],
      pathArray: any[],
      expiryDate?: string | null
    ) => {
      const hasFile = docArray && docArray.length > 0;
      const pathInfo = pathArray && pathArray.length > 0 ? pathArray[0] : null;
      const docInfo = docArray && docArray.length > 0 ? docArray[0] : null;

      // Helper function to get status from pathInfo
      const getStatusFromPathInfo = (pathInfo: any): DocumentStatus => {
        if (!pathInfo || pathInfo.status === undefined) {
          return "not-uploaded" as DocumentStatus;
        }

        switch (pathInfo.status) {
          case 0:
            return "not-uploaded" as DocumentStatus;
          case 1:
            return "pending" as DocumentStatus;
          case 2:
            return "approved" as DocumentStatus;
          case 4:
            return "unapproved" as DocumentStatus;
          default:
            return "not-uploaded" as DocumentStatus;
        }
      };

      // Create file object with proper name from docArray
      const fileName = docInfo?.imageName || "unknown-file";

      // Parse expiry date if provided
      const parsedExpiryDate = expiryDate ? new Date(expiryDate) : null;

      return {
        file: hasFile
          ? new File([""], fileName, {
              type: "application/octet-stream",
            })
          : null,
        expiryDate: parsedExpiryDate,
        status: getStatusFromPathInfo(pathInfo),
      };
    };

    const transformed = {
      sfaLicense: getDocumentData(formData.sfaLicense, formData.sfaLicensePath),
      halalLicense: getDocumentData(
        formData.halalLicense,
        formData.halalLicensePath,
        formData.halalExpire // Pass the expiry date
      ),
      liquorLicense: getDocumentData(
        formData.liquorLicense,
        formData.liquorLicensePath,
        formData.liquorExpire // Pass the expiry date
      ),
      nricFront: getDocumentData(formData.nricFront, formData.nricFrontPath),
      nricBack: getDocumentData(formData.nricBack, formData.nricBackPath),
      letterOfAuth: getDocumentData(
        formData.letterOfAuth,
        formData.letterOfAuthPath
      ),
      storeCover: getDocumentData(formData.storeCover, formData.storeCoverPath),
      storeCoverAR1: getDocumentData(
        formData.storeCoverAR1,
        formData.storeCoverAR1Path
      ),
    };

    return transformed;
  }, [formData]);

  const handleDocumentChange = (field: string, value: any) => {
    // Transform the change back to the original formData structure
    const fieldMapping: { [key: string]: string } = {
      sfaLicense: "sfaLicense",
      halalLicense: "halalLicense",
      liquorLicense: "liquorLicense",
      nricFront: "nricFront",
      nricBack: "nricBack",
      letterOfAuth: "letterOfAuth",
      storeCover: "storeCover",
      storeCoverAR1: "storeCoverAR1",
    };

    // Mapping for expiry date fields
    const expiryFieldMapping: { [key: string]: string } = {
      halalLicense: "halalExpire",
      liquorLicense: "liquorExpire",
    };

    const originalField = fieldMapping[field];
    if (originalField) {
      const pathField = `${originalField}Path`;

      // Handle expiry date changes - this should come first and always execute
      if (value.hasOwnProperty("expiryDate") && expiryFieldMapping[field]) {
        const expiryField = expiryFieldMapping[field];
        // Convert Date to string format that your backend expects
        const expiryDateString = value.expiryDate
          ? new Date(value.expiryDate).toISOString().split("T")[0] // Format as YYYY-MM-DD
          : null;

        onChange(expiryField, expiryDateString);

        // If this is only an expiry date change (no file or status change), return early
        if (!value.hasOwnProperty("file") && !value.hasOwnProperty("status")) {
          return;
        }
      }

      // Handle file deletion case
      if (value.file === null && value.status === "not-uploaded") {
        // Clear the file array
        onChange(originalField, []);
        // Clear/reset the path array
        onChange(pathField, []);

        // Clear expiry date when deleting file
        if (expiryFieldMapping[field]) {
          onChange(expiryFieldMapping[field], null);
        }

        return;
      }

      // Handle file upload case
      if (value.file) {
        // Update the file array
        onChange(originalField, [{ id: 0, imageName: value.file.name }]);

        // Update the status in the path array - include imagePath for consistency
        const statusCode =
          value.status === "pending"
            ? 1
            : value.status === "approved"
            ? 2
            : value.status === "unapproved"
            ? 3
            : 0;

        onChange(pathField, [
          {
            id: 0,
            status: statusCode,
            imagePath: value.file.name, // Add imagePath to path array
          },
        ]);
      }
      // Handle status update only (without file change)
      else if (value.status !== undefined) {
        const currentPath = formData[pathField] || [];
        const statusCode =
          value.status === "not-uploaded"
            ? 0
            : value.status === "pending"
            ? 1
            : value.status === "approved"
            ? 2
            : 3;

        if (currentPath.length > 0) {
          const updatedPath = currentPath.map((item: any, index: number) =>
            index === 0
              ? {
                  ...item,
                  status: statusCode,
                  imagePath: item.imagePath || "unknown-file", // Preserve existing imagePath
                }
              : item
          );
          onChange(pathField, updatedPath);
        }
      }
    }

  };

  const qrCodeRef = useRef<HTMLDivElement>(null);
  const storeName = formData.shopName;
  const unitNumber = formData.floor + "-" + formData.unit;

  const handleDownloadQR = () => {
    if (qrCodeRef.current) {
      const canvas = qrCodeRef.current.querySelector("canvas");
      if (canvas) {
        const pngUrl = canvas
          .toDataURL("image/png")
          .replace("image/png", "image/octet-stream");
        const downloadLink = document.createElement("a");
        downloadLink.href = pngUrl;
        downloadLink.download = `${storeName.replace(/\s+/g, "_")}_QR.png`;
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
      }
    }
  };

  return (
    <ShopDetailsContainer className="h-full">
      <BusinessInfo>
        <DeactivateTitle $mb="0px">Business Information</DeactivateTitle>

        <ProfileInfoItem>
          <InfoTitle>Business Type</InfoTitle>
          <InfoDescription $align="right">
            {formData.businessType}
          </InfoDescription>
        </ProfileInfoItem>
        <ProfileInfoItem>
          <InfoTitle>Shop Name</InfoTitle>
          <InfoDescription $align="right">
            {storeName}
            <QRIcon>
              <QrCodeIcon onClick={() => setShowQrPopover(true)} />
            </QRIcon>
          </InfoDescription>
        </ProfileInfoItem>
        <ProfileInfoItem>
          <InfoTitle>Hawker Name</InfoTitle>
          <InfoDescription $align="right">
            {formData.hawker?.name}
          </InfoDescription>
        </ProfileInfoItem>
        <ProfileInfoItem>
          <InfoTitle>Postal Code</InfoTitle>
          <InfoDescription $align="right">{formData.postcode}</InfoDescription>
        </ProfileInfoItem>
        <ProfileInfoItem>
          <InfoTitle>Street Name</InfoTitle>
          <InfoDescription $align="right">{formData.street}</InfoDescription>
        </ProfileInfoItem>
        <ProfileInfoItem>
          <InfoTitle>Floor</InfoTitle>
          <InfoDescription $align="right">{formData.floor}</InfoDescription>
        </ProfileInfoItem>
        <ProfileInfoItem className="mb-32">
          <InfoTitle>Unit</InfoTitle>
          <InfoDescription $align="right">{formData.unit}</InfoDescription>
        </ProfileInfoItem>

        <BusinessHoursDisplay
          openingHours={openingHours}
          onSave={(updatedHours: any) => {
            onChange("openingHours", updatedHours);
          }}
        />
      </BusinessInfo>

      <ProfileDocumentUpload
        formData={transformedFormData}
        businessType={formData.businessType}
        onChange={handleDocumentChange}
      />

      {showQrPopover && (
        <QrPopover onClick={() => setShowQrPopover(false)}>
          <QrContent onClick={(e) => e.stopPropagation()}>
            <CloseButtonBusInfo onClick={() => setShowQrPopover(false)}>
              <CloseIcon />
            </CloseButtonBusInfo>
            <div ref={qrCodeRef}>
              <QrCodeContainer>
                <QRCodeSVG
                  value={`https://example.com/store/${storeName.replace(
                    /\s+/g,
                    "-"
                  )}`}
                  size={200}
                  level="H"
                  includeMargin={true}
                />
              </QrCodeContainer>
            </div>
            <StoreName>
              {storeName}, #{unitNumber}
            </StoreName>
            <DownloadButtonQR onClick={handleDownloadQR}>
              <FileDownloadIcon /> Download QR
            </DownloadButtonQR>
          </QrContent>
        </QrPopover>
      )}
    </ShopDetailsContainer>
  );
};

export default ShopDetails;
